import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withIndexedDbSync } from './features/with-indexed-db-sync';
import { DEFAULT_REPORTS_STORE, IndexedDbStorageService } from './storage/indexed-db.service';

export interface MenuCacheEntry<T = unknown> {
  menuRoute: string;
  cacheKey: string;
  data: T;
  timestamp: number;
  expiresAt: number;
}

export interface MenuDataState {
  activeMenuRoute: string;
  entries: Record<string, MenuCacheEntry<unknown>>;
  menuLastUpdated: Record<string, number>;
}

export const DEFAULT_MENU_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes default TTL

const initialMenuDataState: MenuDataState = {
  activeMenuRoute: '',
  entries: {},
  menuLastUpdated: {}
};

export const MenuDataStore = signalStore(
  { providedIn: 'root' },
  withState(initialMenuDataState),
  withIndexedDbSync<MenuDataState>({
    key: 'gars_menu_data_store',
    storeName: DEFAULT_REPORTS_STORE,
    debounceMs: 250
  }),
  withComputed((store) => ({
    /**
     * Total number of cached records currently stored across all menu items
     */
    totalCachedRecords: computed(() => Object.keys(store.entries()).length),

    /**
     * Whether the currently active menu has cached data available
     */
    isCurrentMenuCached: computed(() => {
      const active = store.activeMenuRoute();
      if (!active) return false;
      const allEntries = Object.values(store.entries());
      return allEntries.some(e => e.menuRoute === active && Date.now() < e.expiresAt);
    }),

    /**
     * Timestamp of when the current menu item was last updated
     */
    currentMenuLastUpdated: computed(() => {
      const active = store.activeMenuRoute();
      return active ? store.menuLastUpdated()[active] ?? null : null;
    })
  })),
  withMethods((store, indexedDb = inject(IndexedDbStorageService)) => ({
    /**
     * Set the current active route/menu item
     */
    setActiveMenuRoute(route: string): void {
      const normalized = normalizeMenuRoute(route);
      if (store.activeMenuRoute() !== normalized) {
        patchState(store, { activeMenuRoute: normalized });
      }
    },

    /**
     * Record API response data for a menu item
     */
    recordData<T>(menuRoute: string, cacheKey: string, data: T, ttlMs: number = DEFAULT_MENU_CACHE_TTL_MS): void {
      const normalizedRoute = normalizeMenuRoute(menuRoute);
      const now = Date.now();
      const entry: MenuCacheEntry<T> = {
        menuRoute: normalizedRoute,
        cacheKey,
        data,
        timestamp: now,
        expiresAt: now + ttlMs
      };

      patchState(store, (state) => ({
        entries: {
          ...state.entries,
          [cacheKey]: entry as MenuCacheEntry<unknown>
        },
        menuLastUpdated: {
          ...state.menuLastUpdated,
          [normalizedRoute]: now
        }
      }));
    },

    /**
     * Retrieve cached data if present and not expired
     */
    getData<T>(cacheKey: string, maxAgeMs?: number): T | null {
      const entry = store.entries()[cacheKey];
      if (!entry) return null;

      const now = Date.now();
      if (now > entry.expiresAt) {
        return null;
      }

      if (maxAgeMs !== undefined && (now - entry.timestamp) > maxAgeMs) {
        return null;
      }

      return entry.data as T;
    },

    /**
     * Check if valid, non-expired data is present
     */
    hasValidData(cacheKey: string, maxAgeMs?: number): boolean {
      const entry = store.entries()[cacheKey];
      if (!entry) return false;

      const now = Date.now();
      if (now > entry.expiresAt) return false;
      if (maxAgeMs !== undefined && (now - entry.timestamp) > maxAgeMs) return false;

      return true;
    },

    /**
     * Invalidate all recorded data for a specific menu item (e.g. after a refresh/sync)
     * If no route is provided, invalidates the currently active menu route.
     */
    invalidateMenu(menuRoute?: string): void {
      const targetRoute = normalizeMenuRoute(menuRoute || store.activeMenuRoute());
      if (!targetRoute) return;

      patchState(store, (state) => {
        const nextEntries: Record<string, MenuCacheEntry<unknown>> = {};
        for (const [key, entry] of Object.entries(state.entries)) {
          if (entry.menuRoute !== targetRoute) {
            nextEntries[key] = entry;
          }
        }

        const nextLastUpdated = { ...state.menuLastUpdated };
        delete nextLastUpdated[targetRoute];

        return {
          entries: nextEntries,
          menuLastUpdated: nextLastUpdated
        };
      });
    },

    /**
     * Invalidate a single cache key
     */
    invalidateKey(cacheKey: string): void {
      patchState(store, (state) => {
        const nextEntries = { ...state.entries };
        delete nextEntries[cacheKey];
        return { entries: nextEntries };
      });
    },

    /**
     * Clear all recorded menu item caches completely
     */
    clearAll(): void {
      patchState(store, {
        entries: {},
        menuLastUpdated: {}
      });
    }
  }))
);

/**
 * Normalizes route paths into canonical menu identifiers, e.g.:
 * '/app/workspace-analytics' -> 'workspace-analytics'
 * '/app/gemini-intelligence' -> 'gemini-intelligence'
 */
export function normalizeMenuRoute(route: string | undefined | null): string {
  if (!route) return 'general';
  let cleaned = route.trim().toLowerCase();
  if (cleaned.startsWith('/')) cleaned = cleaned.substring(1);
  if (cleaned.startsWith('app/')) cleaned = cleaned.substring(4);
  const slashIdx = cleaned.indexOf('/');
  if (slashIdx !== -1) cleaned = cleaned.substring(0, slashIdx);
  const qIdx = cleaned.indexOf('?');
  if (qIdx !== -1) cleaned = cleaned.substring(0, qIdx);
  return cleaned || 'home';
}
